import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import type {
  ArrayParamWhitelists,
  DateRangeValue,
  DecodeArrayParamsResult,
  DecodeDateRangeResult,
} from "./types";

/**
 * The single owner of the URL-param wire format. No code outside this module
 * should read or write the query string — callers exchange only typed,
 * validated values with the functions below.
 */

// `customParseFormat` is required for the strict `(value, format, true)` form
// below — without it, dayjs silently falls back to native `Date` parsing and
// ignores the format/strict arguments entirely, which would accept malformed
// strings like ISO timestamps as a valid `YYYY-MM-DD` date.
dayjs.extend(customParseFormat);

const DATE_FORMAT = "YYYY-MM-DD";

/** Coerces any value TanStack's search decoder might hand back (string, number,
 * boolean, or an array of those from repeated keys) down to its string form,
 * recursing into arrays. */
const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.flatMap(toStringList) : [String(value)];

/** Splits a raw decoded search value into deduped, trimmed, non-empty tokens.
 * Accepts (and normalizes) all three tolerant wire shapes: a single
 * comma-joined string, an array from repeated keys, or a mix of both. */
const splitTokens = (raw: unknown): string[] => {
  const tokens = toStringList(raw)
    .flatMap((s) => s.split(","))
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return Array.from(new Set(tokens));
};

/**
 * Tolerant reader: normalizes TanStack's decoded search record — which may
 * contain comma-joined strings, real arrays (repeated keys), a mix of both,
 * and number/boolean-coerced scalars — into the canonical wire shape: one
 * comma-joined string per array key, and a plain string per scalar key.
 *
 * This is the only function meant to see the router's raw decoded search
 * record; it belongs in a route's `validateSearch`. It does shape validation
 * only — never whitelist validation (option sets are async and can't be
 * awaited there); see `decodeArrayParams` for that.
 */
export const normalizeSearch = (
  raw: Record<string, unknown>,
  arrayKeys: readonly string[],
  scalarKeys: readonly string[],
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const key of arrayKeys) {
    if (!(key in raw) || raw[key] == null) continue;
    const tokens = splitTokens(raw[key]);
    if (tokens.length > 0) result[key] = tokens.join(",");
  }

  for (const key of scalarKeys) {
    if (!(key in raw) || raw[key] == null) continue;
    const tokens = toStringList(raw[key]);
    const value = tokens[0]?.trim();
    if (value) result[key] = value;
  }

  return result;
};

/**
 * Whitelists each facet's canonical comma-joined wire value against its
 * known option set. Per-facet policy: some values valid -> keep the valid
 * ones and report the rest as dropped; every value invalid -> report zero
 * values for that facet AND flag it in `fullyInvalidFacets`, so the caller
 * can force a clean empty state instead of silently treating "all invalid"
 * the same as "no filter" (an empty array is indistinguishable from "not
 * filtered" once it reaches the API).
 */
export const decodeArrayParams = <TKeys extends string>(
  // Typed as an unconstrained string-keyed record (not `Partial<Record<TKeys, string>>`)
  // so TypeScript infers `TKeys` from `whitelists` alone — the caller's wire
  // record legitimately carries other keys (e.g. `startDate`/`endDate`) that
  // have nothing to do with this facet whitelist.
  wire: Record<string, string | undefined>,
  whitelists: ArrayParamWhitelists<TKeys>,
): DecodeArrayParamsResult<TKeys> => {
  const values = {} as DecodeArrayParamsResult<TKeys>["values"];
  const dropped = {} as DecodeArrayParamsResult<TKeys>["dropped"];
  const fullyInvalidFacets: TKeys[] = [];

  for (const key of Object.keys(whitelists) as TKeys[]) {
    const tokens = splitTokens(wire[key] ?? "");
    const allowed = new Set(whitelists[key]);
    const valid = tokens.filter((t) => allowed.has(t));
    const invalid = tokens.filter((t) => !allowed.has(t));

    values[key] = valid;
    dropped[key] = invalid;
    if (tokens.length > 0 && valid.length === 0) {
      fullyInvalidFacets.push(key);
    }
  }

  return { values, dropped, fullyInvalidFacets };
};

/** Strict `YYYY-MM-DD` parse — no partial/ISO/locale leniency, matching the
 * existing portal `formatDate` this codec round-trips against. */
const parseStrictDate = (value: string | undefined): Date | null => {
  if (!value) return null;
  const parsed = dayjs(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Validates `startDate`/`endDate` (AD-8): both must be present, strict
 * `YYYY-MM-DD`, and `from <= to`. Any other case — missing, partial,
 * malformed, or reversed — falls back to `defaultRange` rather than
 * erroring, per AC-4's "no errors" requirement.
 */
export const decodeDateRange = (
  wire: { startDate?: string; endDate?: string },
  defaultRange: DateRangeValue,
): DecodeDateRangeResult => {
  const from = parseStrictDate(wire.startDate);
  const to = parseStrictDate(wire.endDate);

  if (from && to && from.getTime() <= to.getTime()) {
    return { range: { from, to }, usedDefault: false };
  }
  return { range: defaultRange, usedDefault: true };
};

/**
 * Strict writer: emits the canonical comma-joined wire shape for array
 * facets and `YYYY-MM-DD` for dates. Empty/null facets are omitted entirely
 * rather than emitted as `?facet=` — an empty value must never be mistaken
 * for "no filter" on decode (see `decodeArrayParams`).
 */
export const encodeSearch = (
  arrayValues: Record<string, string[] | null | undefined>,
  dateRange: { from: Date | null; to: Date | null },
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, values] of Object.entries(arrayValues)) {
    if (values && values.length > 0) {
      result[key] = values.join(",");
    }
  }

  if (dateRange.from) {
    result.startDate = dayjs(dateRange.from).format(DATE_FORMAT);
  }
  if (dateRange.to) {
    result.endDate = dayjs(dateRange.to).format(DATE_FORMAT);
  }

  return result;
};
